/**
 * Wizard step 3 — Sponsors & default consolation (§16.1, §7, §8): sponsors from the catalogue with tier,
 * placements, reveal skin, prize linkage; and "Sponsor-default prize for eligible non-winners" per sponsor:
 * product pick, first-N or custom range, qualifying entry types, one-per-participant cap, priority, reserved
 * units with a live stock check, and a reveal copy editor with {sponsor}/{product}/{mainPrize} variables.
 */
import { useQuery } from '@tanstack/react-query';
import {
  EntrySource,
  renderTemplate,
  RevealSkin,
  samplingPositions,
  SAMPLING_COPY_VARIABLES,
  SponsorPlacement,
  SponsorTier,
  validateSamplingCopy,
  type PrizeConfig,
  type SamplingRule,
  type SponsorConfig,
} from '@windrop/contracts';
import { Button, Field, Input, Select, Switch } from '@windrop/ui';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FormErrorSummary, RhfSelect, RhfText, fieldId } from '../../../components/FormField';
import { useWizard } from '../../lotteries/wizard/WizardFrame';
import { stepForPath, type DraftConfigInput } from '../../lotteries/wizard/steps';
import { catalogApi } from '../api';

const newRefId = (): string => {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const bytes = new Uint8Array(26);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % 32])
    .join('')
    .replace(/^./, '0');
};

const DEFAULT_COPY = "You didn't win the {mainPrize} this time — but you've won {product} from {sponsor}.";

/** Pure preview used by the step and its test: rendered copy plus validation problems. */
export function previewSamplingCopy(
  template: string,
  vars: { sponsor: string; product: string; mainPrize: string },
): { text: string; problems: string[] } {
  return { text: renderTemplate(template, vars), problems: validateSamplingCopy(template) };
}

export function SponsorsStep() {
  const { control, setValue, getValues } = useFormContext<DraftConfigInput>();
  const sponsorsArray = useFieldArray({ control, name: 'sponsors.sponsors' });
  const rulesArray = useFieldArray({ control, name: 'sampling.rules' });
  const sponsors = (useWatch({ control, name: 'sponsors.sponsors' }) as SponsorConfig[] | undefined) ?? [];
  const rules = (useWatch({ control, name: 'sampling.rules' }) as SamplingRule[] | undefined) ?? [];
  const prizes = (useWatch({ control, name: 'prizes.prizes' }) as PrizeConfig[] | undefined) ?? [];
  const { issues, goToStep } = useWizard();
  const mine = issues.filter((i) => i.step === 'sponsors');
  const catalogue = useQuery({ queryKey: ['sponsors'], queryFn: () => catalogApi.sponsors.list() });
  const mainPrize = prizes.find((p) => p.tier === 'GRAND') ?? prizes[0];

  const addSponsor = (sponsorId: string) => {
    const s = catalogue.data?.find((x) => x.sponsorId === sponsorId);
    if (!s || sponsors.some((x) => x.sponsorId === sponsorId)) return;
    sponsorsArray.append({
      sponsorId: s.sponsorId,
      name: s.name,
      tier: s.tier,
      displayWeight: s.displayWeight,
      placements:
        s.tier === 'TITLE'
          ? ['HERO_LOCKUP', 'SPONSOR_STRIP', 'WIN_CREDIT', 'SHARE_CARD']
          : s.tier === 'GOLD'
            ? ['SPONSOR_STRIP', 'PRIZE_BADGE']
            : ['FOOTER_STRIP'],
      logoLightRef: s.logoLightRef,
      logoDarkRef: s.logoDarkRef,
      brandColor: s.brandColor,
      tagline: s.tagline,
      websiteUrl: s.websiteUrl,
      ctaLabel: s.ctaLabel,
      ctaUrl: s.ctaUrl,
      sponsoredPrizeIds: [],
      revealSkin: 'NONE',
    } as never);
  };

  const ruleFor = (sponsorId: string) => rules.findIndex((r) => r.sponsorId === sponsorId);
  const toggleRule = (sponsorId: string, on: boolean) => {
    const idx = ruleFor(sponsorId);
    if (on && idx === -1) {
      const product =
        prizes.find((p) => p.tier === 'SPONSOR_SAMPLE' && p.sponsorId === sponsorId) ??
        prizes.find((p) => p.sponsorId === sponsorId);
      rulesArray.append({
        ruleId: newRefId(),
        version: 1,
        enabled: true,
        sponsorId,
        defaultPrizeId: product?.prizeId ?? '',
        eligible: { firstN: Math.max(1, product?.quantity ?? 100) },
        countingMode: 'UNIQUE_PARTICIPANT',
        qualifyingEntryTypes: ['FREE', 'PAID'],
        onePerParticipant: true,
        reservedUnits: product?.quantity ?? 100,
        priority: rules.length + 1,
        copy: { reveal: { en: DEFAULT_COPY } },
        mainWinnerExclusion: true,
      } as never);
    } else if (!on && idx >= 0) rulesArray.remove(idx);
  };

  return (
    <div className="wd-stack" data-testid="sponsors-step">
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in sponsors & sampling`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />
      <Field label="Add a sponsor" hint="Sponsors never cover gameplay controls or alter outcomes (§7).">
        <Select
          data-testid="sponsor-pick"
          value=""
          onChange={(e) => e.target.value && addSponsor(e.target.value)}
          options={(catalogue.data ?? []).map((s) => ({
            value: s.sponsorId,
            label: `${s.name} · ${s.tier}`,
          }))}
          placeholder={catalogue.isPending ? 'Loading…' : 'Choose a sponsor'}
        />
      </Field>
      {sponsorsArray.fields.map((f, i) => {
        const s = sponsors[i];
        const ri = s ? ruleFor(s.sponsorId) : -1;
        const rule = ri >= 0 ? rules[ri] : undefined;
        return (
          <fieldset key={f.id} className="wd-surface" style={{ padding: 16 }} data-testid={`sponsor-${i}`}>
            <legend>
              <strong>{s?.name ?? 'Sponsor'}</strong>
            </legend>
            <div className="wdc-form-grid">
              <RhfSelect
                control={control}
                name={`sponsors.sponsors.${i}.tier`}
                label="Tier"
                options={SponsorTier.options.map((t) => ({ value: t, label: t }))}
              />
              <RhfText
                control={control}
                name={`sponsors.sponsors.${i}.displayWeight`}
                label="Display weight"
                type="number"
                parse={(r) => Number(r) || 1}
              />
              <RhfSelect
                control={control}
                name={`sponsors.sponsors.${i}.revealSkin`}
                label="Reveal skin"
                options={RevealSkin.options.map((r) => ({ value: r, label: r }))}
              />
              <Field
                label="Placements (§7.1)"
                hint="Every sponsored placement is visibly labelled; impressions count only when viewable."
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                  {SponsorPlacement.options.map((pl) => (
                    <label key={pl}>
                      <input
                        type="checkbox"
                        checked={(s?.placements ?? []).includes(pl)}
                        onChange={(e) => {
                          const cur = s?.placements ?? [];
                          setValue(
                            `sponsors.sponsors.${i}.placements`,
                            e.target.checked ? [...cur, pl] : cur.filter((x) => x !== pl),
                            { shouldDirty: true, shouldValidate: true },
                          );
                        }}
                      />{' '}
                      {pl.replace(/_/g, ' ').toLowerCase()}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Sponsored prizes" hint='Shows "Sponsored by" on the prize (§7.1).'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {prizes.map((p) => (
                    <label key={p.prizeId}>
                      <input
                        type="checkbox"
                        checked={(s?.sponsoredPrizeIds ?? []).includes(p.prizeId)}
                        onChange={(e) => {
                          const cur = s?.sponsoredPrizeIds ?? [];
                          setValue(
                            `sponsors.sponsors.${i}.sponsoredPrizeIds`,
                            e.target.checked ? [...cur, p.prizeId] : cur.filter((x) => x !== p.prizeId),
                            { shouldDirty: true },
                          );
                        }}
                      />{' '}
                      {p.name?.en ?? p.prizeId}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
            <h3>Sponsor-default prize for eligible non-winners (§8)</h3>
            <Switch
              label="Enable for this sponsor"
              checked={Boolean(rule)}
              onCheckedChange={(on) => s && toggleRule(s.sponsorId, on)}
              onLabel="Enabled"
              offLabel="Off"
              data-testid={`sampling-toggle-${i}`}
            />
            {rule && ri >= 0 ? (
              <SamplingRuleEditor
                index={ri}
                rule={rule}
                sponsorName={s?.name ?? ''}
                prizes={prizes}
                mainPrizeName={mainPrize?.name?.en ?? 'main prize'}
              />
            ) : null}
            <Button
              variant="quiet"
              onClick={() => {
                if (s) toggleRule(s.sponsorId, false);
                sponsorsArray.remove(i);
              }}
            >
              Remove sponsor
            </Button>
          </fieldset>
        );
      })}
      {sponsorsArray.fields.length === 0 ? (
        <p className="wd-muted">No sponsors yet — the lottery runs without sponsor placements.</p>
      ) : null}
      <input type="hidden" value={JSON.stringify(getValues('sampling'))} readOnly aria-hidden />
    </div>
  );
}

function SamplingRuleEditor({
  index,
  rule,
  sponsorName,
  prizes,
  mainPrizeName,
}: {
  index: number;
  rule: SamplingRule;
  sponsorName: string;
  prizes: PrizeConfig[];
  mainPrizeName: string;
}) {
  const { control, setValue } = useFormContext<DraftConfigInput>();
  const base = `sampling.rules.${index}` as const;
  const product = prizes.find((p) => p.prizeId === rule.defaultPrizeId);
  const stock = useQuery({
    queryKey: ['prize-stock', rule.defaultPrizeId],
    queryFn: () => catalogApi.prizes.stock(rule.defaultPrizeId),
    enabled: Boolean(rule.defaultPrizeId),
    retry: false,
  });
  const { start, end } = samplingPositions(rule.eligible ?? { firstN: 1 });
  const size = Math.max(0, end - start + 1);
  const mode: 'firstN' | 'range' = rule.eligible && 'range' in rule.eligible ? 'range' : 'firstN';
  const copy = rule.copy?.reveal?.en ?? '';
  const preview = previewSamplingCopy(copy, {
    sponsor: sponsorName,
    product: product?.name?.en ?? 'the sponsor product',
    mainPrize: mainPrizeName,
  });
  const coverable = stock.data ? stock.data.samplingReserved + stock.data.available : null;
  const short = size > (rule.reservedUnits ?? 0);
  const overStock = coverable !== null && (rule.reservedUnits ?? 0) > coverable;
  return (
    <div className="wd-stack" data-testid={`sampling-rule-${index}`}>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name={`${base}.defaultPrizeId`}
          label="Sponsor product"
          required
          hint="Any sponsor-funded product from the catalogue; winners still pick colour or model."
          options={prizes.map((p) => ({ value: p.prizeId, label: `${p.name?.en ?? p.prizeId} (${p.tier})` }))}
        />
        <Field
          label="Participant range"
          hint="Positions are assigned once, at the first qualified entry (§8.1)."
        >
          <Select
            value={mode}
            onChange={(e) =>
              setValue(
                `${base}.eligible`,
                (e.target.value === 'range' ? { range: { start: 1, end } } : { firstN: end }) as never,
                { shouldDirty: true, shouldValidate: true },
              )
            }
            options={[
              { value: 'firstN', label: 'First N unique qualified participants' },
              { value: 'range', label: 'Custom start–end range' },
            ]}
            data-testid={`range-mode-${index}`}
          />
        </Field>
        {mode === 'firstN' ? (
          <RhfText
            control={control}
            name={`${base}.eligible.firstN`}
            label="N"
            required
            type="number"
            parse={(r) => Number(r) || 1}
          />
        ) : (
          <>
            <RhfText
              control={control}
              name={`${base}.eligible.range.start`}
              label="Range start"
              required
              type="number"
              parse={(r) => Number(r) || 1}
            />
            <RhfText
              control={control}
              name={`${base}.eligible.range.end`}
              label="Range end"
              required
              type="number"
              parse={(r) => Number(r) || 1}
            />
          </>
        )}
        <RhfSelect
          control={control}
          name={`${base}.countingMode`}
          label="Counting mode (D12)"
          options={[
            { value: 'UNIQUE_PARTICIPANT', label: 'Unique participants, first-qualified-entry order' },
            { value: 'QUALIFIED_ENTRY', label: 'Qualified entries' },
          ]}
        />
        <Field label="Qualifying entries" hint="Paid-only needs prominent disclosure (D12).">
          <div style={{ display: 'flex', gap: 12 }}>
            {EntrySource.options.map((src) => (
              <label key={src}>
                <input
                  type="checkbox"
                  checked={(rule.qualifyingEntryTypes ?? []).includes(src)}
                  onChange={(e) => {
                    const cur = rule.qualifyingEntryTypes ?? [];
                    setValue(
                      `${base}.qualifyingEntryTypes`,
                      (e.target.checked ? [...cur, src] : cur.filter((x) => x !== src)) as never,
                      { shouldDirty: true, shouldValidate: true },
                    );
                  }}
                />{' '}
                {src}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Cap">
          <Input value="One sponsor-default prize per participant per lottery" readOnly />
        </Field>
        <RhfText
          control={control}
          name={`${base}.priority`}
          label="Priority"
          required
          type="number"
          hint="Lower evaluates first; must be unique across sponsors."
          parse={(r) => Number(r) || 1}
        />
        <RhfText
          control={control}
          name={`${base}.reservedUnits`}
          label="Reserved units"
          required
          type="number"
          hint="Reserved before publication; must cover the range."
          parse={(r) => Number(r) || 0}
        />
      </div>
      <p data-testid={`stock-check-${index}`} className={short || overStock ? 'wd-error' : 'wd-muted'}>
        Range covers {size} position{size === 1 ? '' : 's'}; {rule.reservedUnits ?? 0} reserved
        {coverable !== null
          ? ` · ${coverable} unit${coverable === 1 ? '' : 's'} coverable in stock (${stock.data!.samplingReserved} already reserved for sampling, ${stock.data!.available} free)`
          : stock.isError
            ? ' · stock unknown until the prize is saved to the catalogue'
            : ''}
        {short
          ? ' — publication is blocked until reserved units ≥ N (§8).'
          : overStock
            ? ' — not enough units in stock.'
            : ' — covered.'}
      </p>
      <Field
        label="Reveal copy"
        hint={`Variables: ${SAMPLING_COPY_VARIABLES.map((v) => `{${v}}`).join(' ')}. Participants always see the real product name.`}
        id={fieldId(`${base}.copy.reveal.en`)}
      >
        <textarea
          className="wd-input"
          rows={3}
          value={copy}
          data-testid={`copy-${index}`}
          onChange={(e) =>
            setValue(`${base}.copy.reveal.en`, e.target.value, { shouldDirty: true, shouldValidate: true })
          }
        />
      </Field>
      <div
        className="wd-surface"
        style={{ padding: 12 }}
        data-testid={`copy-preview-${index}`}
        aria-live="polite"
      >
        <p className="wd-muted">Preview on the reveal screen</p>
        <p>
          <strong>{preview.text}</strong>
        </p>
        {preview.problems.length ? (
          <ul role="alert">
            {preview.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
